import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';

import {
  SR_API_ACTION_UTILITY_CHECK_LOGIN_EXIST,
  SR_API_ACTION_UTILITY_VERIFY_PHONE_SMS_CODE_REQUESTED,
} from 'api/utilityActionTypes';

import { initialApiActionState } from 'api/createApiClient';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import useBranch from 'client/hooks/useBranch';
import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import SrBox from 'client/components/SrBox';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_RED,
} from 'client/components/SrErrorMessage/SrErrorMessage';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

const getFormFields = () => {
  return [
    {
      id: 'firstName',
      title: 'First Name',
      type: 'short_text',
      properties: {
        placeholder: 'type your first name...',
      },
      validations: ['required'],
    },
    {
      id: 'lastName',
      title: 'Last Name',
      type: 'short_text',
      properties: {
        placeholder: 'type your last name...',
      },
      validations: ['required'],
    },
    {
      id: 'phone',
      title: 'Phone',
      type: 'phone',
      properties: {
        placeholder: 'type your phone...',
      },
      validations: ['required', 'phone'],
    },
    {
      id: 'email',
      title: 'Email',
      type: 'email',
      properties: {
        placeholder: 'type your email...',
      },
      validations: ['required', 'email'],
    },
    {
      id: 'leaseStartDate',
      title: 'Lease Start Date',
      type: 'date_picker',
      properties: {
        placeholder: 'Select your date...',
      },
      validations: ['required'],
    },
  ];
};

const SrUtilitySetupLazySignupModal = ({ defaultValues, onSubmit, onClose }) => {
  const dispatch = useDispatch();
  const [signupError, setSignupError] = useState(null);

  const { utilityVerifyPhoneSmsApiState = initialApiActionState } = useBranch({
    utilityVerifyPhoneSmsApiState: [
      'apiState',
      SR_API_ACTION_UTILITY_VERIFY_PHONE_SMS_CODE_REQUESTED,
      'default',
    ],
  });

  const formFields = getFormFields();

  const onValidateUser = async (formValues) => {
    setSignupError(null);
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_CHECK_LOGIN_EXIST,
      payload: {
        email: formValues.email,
        phone: formValues.phone,
      },
      onSuccess: ({ exists, phone }) => {
        if (exists && phone) {
          onSubmit({
            ...formValues,
            loginExist: true,
          });
          return;
        }

        if (exists) {
          const error = phone ? 'phone number' : 'email';
          setSignupError(`There is already an account with that ${error}.`);
        } else {
          onSubmit(formValues);
        }
      },
    });
  };

  return (
    <Formik
      initialValues={createInitialValues({
        formFields,
        defaultValues: defaultValues,
      })}
      validate={createFormValidationFn(formFields)}
      onSubmit={onValidateUser}
      render={(formikBag) => {
        return (
          <SrSimpleMessageModal
            underline
            maxWidth={375}
            messageClassName="noAlign"
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            headline="Confirm your lease info to continue:"
            message={
              <div>
                {formFields.map((formField) => (
                  <SrFormikField
                    key={formField.id}
                    formField={formField}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                ))}
                <SrBox marginTop="10px">
                  {utilityVerifyPhoneSmsApiState.error && (
                    <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_RED}>
                      {utilityVerifyPhoneSmsApiState.error.message}
                    </SrErrorMessage>
                  )}
                  {signupError && (
                    <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_RED}>
                      {signupError}
                    </SrErrorMessage>
                  )}
                </SrBox>
              </div>
            }
            srMultiButtons={[
              {
                buttonText: 'Confirm',
                onButtonClick: formikBag.handleSubmit,
                buttonTheme: SR_BUTTON_THEME_ADOBE_GREEN,
                buttonDisabled: utilityVerifyPhoneSmsApiState.inProgress,
                buttonId: 'srConfirmLeaseInfoButton',
              },
              {
                buttonText: 'Cancel',
                onButtonClick: onClose,
                buttonTheme: SR_BUTTON_THEME_ADOBE_GREY,
                buttonDisabled: utilityVerifyPhoneSmsApiState.inProgress,
              },
            ]}
          />
        );
      }}
    />
  );
};

SrUtilitySetupLazySignupModal.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

SrUtilitySetupLazySignupModal.displayName = 'SrUtilitySetupLazySignupModal';

export default SrUtilitySetupLazySignupModal;
